import { RouteWithGates } from '../api/routes';
import { EARTH_RADIUS_M, toRad } from './geoDistance';

/**
 * How far apart two routes' measured sections may be and still be merged.
 * Mirrors the server: AverageRoute and the gate re-projection both look for
 * intersections within CombineRoutes' max_radius of 100 m, so routes further
 * apart than this produce a merged route with no gates on it.
 */
export const ROUTE_MERGE_MAX_DISTANCE_M = 100;

export interface RouteMergeExclusion {
  route: RouteWithGates;
  /** Metres to the nearest candidate's section, or null when the route has no geometry. */
  distanceM: number | null;
}

export interface RouteMergeCandidates {
  candidates: RouteWithGates[];
  excluded: RouteMergeExclusion[];
}

type Point = { x: number; y: number };
type Section = { a: Point; b: Point };

/**
 * A route's measured section, in metres on a local flat projection: the line
 * between its origin gate's two points, or its first gate's when the route
 * has no origin gates. Null when neither exists.
 */
function routeSection(route: RouteWithGates, lat0: number): Section | null {
  const hasOrigin = (route.origin_gates_lat?.length ?? 0) >= 2 && (route.origin_gates_lon?.length ?? 0) >= 2;
  const lats = hasOrigin ? route.origin_gates_lat : route.gates?.[0]?.gates_lat;
  const lons = hasOrigin ? route.origin_gates_lon : route.gates?.[0]?.gates_lon;
  if (!lats || !lons || lats.length < 2 || lons.length < 2) return null;
  const toPoint = (lat: number, lon: number): Point => ({
    x: toRad(lon) * Math.cos(toRad(lat0)) * EARTH_RADIUS_M,
    y: toRad(lat) * EARTH_RADIUS_M,
  });
  return { a: toPoint(lats[0], lons[0]), b: toPoint(lats[1], lons[1]) };
}

function pointToSegment(p: Point, s: Section): number {
  const dx = s.b.x - s.a.x;
  const dy = s.b.y - s.a.y;
  const lenSq = dx * dx + dy * dy;
  let t = lenSq === 0 ? 0 : ((p.x - s.a.x) * dx + (p.y - s.a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = s.a.x + t * dx - p.x;
  const cy = s.a.y + t * dy - p.y;
  return Math.sqrt(cx * cx + cy * cy);
}

/** Smallest gap between two sections: either endpoint of one to the other line. */
function sectionDistance(s1: Section, s2: Section): number {
  return Math.min(
    pointToSegment(s1.a, s2), pointToSegment(s1.b, s2),
    pointToSegment(s2.a, s1), pointToSegment(s2.b, s1),
  );
}

/**
 * Split a selection of routes into the ones that can be merged together and
 * the ones that cannot. Two routes are merge candidates when their measured
 * sections come within ROUTE_MERGE_MAX_DISTANCE_M of each other; the
 * candidates are the largest such connected group. Everything else is
 * excluded, with its distance to the nearest candidate so the UI can say why.
 */
export function findRouteMergeCandidates(routes: RouteWithGates[]): RouteMergeCandidates {
  const lat0 = routes
    .map((r) => r.origin_gates_lat?.[0] ?? r.gates?.[0]?.gates_lat?.[0])
    .find((lat): lat is number => lat != null) ?? 0;
  const sections = routes.map((r) => routeSection(r, lat0));

  const n = routes.length;
  const dist: number[][] = routes.map(() => routes.map(() => Infinity));
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const si = sections[i];
      const sj = sections[j];
      if (!si || !sj) continue;
      dist[i][j] = dist[j][i] = sectionDistance(si, sj);
    }
  }

  // Connected components over the "close enough" relation, largest wins.
  const component = new Array<number>(n).fill(-1);
  const groups: number[][] = [];
  for (let i = 0; i < n; i++) {
    if (component[i] !== -1 || !sections[i]) continue;
    const members: number[] = [];
    const stack = [i];
    component[i] = groups.length;
    while (stack.length) {
      const k = stack.pop() as number;
      members.push(k);
      for (let m = 0; m < n; m++) {
        if (component[m] === -1 && dist[k][m] <= ROUTE_MERGE_MAX_DISTANCE_M) {
          component[m] = groups.length;
          stack.push(m);
        }
      }
    }
    groups.push(members);
  }
  const best = groups.reduce<number[]>((acc, g) => (g.length > acc.length ? g : acc), []);
  const bestSet = new Set(best);

  const candidates = best.length >= 2 ? best.sort((a, b) => a - b).map((i) => routes[i]) : [];
  const candidateSet = candidates.length ? bestSet : new Set<number>();
  const excluded: RouteMergeExclusion[] = [];
  for (let i = 0; i < n; i++) {
    if (candidateSet.has(i)) continue;
    let nearest = Infinity;
    for (const j of candidateSet) nearest = Math.min(nearest, dist[i][j]);
    excluded.push({
      route: routes[i],
      distanceM: sections[i] && Number.isFinite(nearest) ? Math.round(nearest) : null,
    });
  }
  return { candidates, excluded };
}
